import { createSettingsParams, SettingsParamType } from '@wix/tpa-settings';
import { DataType } from '@wix/editor-platform-ooi-metadata-resolver';

import {
  ADD_TO_CART_BUTTON_PLACEMENT,
  HEADER_PLACEMENT,
  ITEM_IMAGE_PLACEMENT,
  ITEM_IMAGE_RATIO,
  ITEM_IMAGE_RESIZE,
  ITEM_LAYOUT_TYPE,
} from '../../../constants';

export type LayoutSettingsParams = {
  itemLayoutType: SettingsParamType.Number;
  itemImagePlacement: SettingsParamType.Number;
  itemImageRatio: SettingsParamType.Number;
  itemImageResize: SettingsParamType.Number;
  headerImagePlacement: SettingsParamType.Number;
  addToCartButtonPlacement: SettingsParamType.Number;
};

// Declared inline (not via `layoutSettingsParams.itemLayoutType`) because
// `itemImageRatio` needs it inside the same `createSettingsParams` call, and
// referencing the exported const in its own initializer makes it implicitly
// `any` for TypeScript. `key` must stay in sync with the object key below.
const itemLayoutTypeParam = {
  key: 'itemLayoutType',
  type: SettingsParamType.Number,
  getDefaultValue: () => ITEM_LAYOUT_TYPE.STRIP,
};

/**
 * `metadata` exposes these params in the widget's editor manifest
 * (`editorElement.data`), which is what Aria reads and edits in Studio 2.
 * Params without `metadata` are dropped from the manifest. Aria writes each
 * param on its own, so the layout description tells it which related params
 * the Settings panel changes together with the layout (see MenuLayout).
 */
export const layoutSettingsParams = createSettingsParams<LayoutSettingsParams>({
  itemLayoutType: {
    type: SettingsParamType.Number,
    getDefaultValue: () => ITEM_LAYOUT_TYPE.STRIP,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: ITEM_LAYOUT_TYPE.STRIP,
      },
      dataItemManifestData: {
        displayName: t('app.settings.layout.menu.choose-layout'),
      },
      ai: {
        description:
          'Menu item layout. Only two layouts exist: 0 = Strip (list / rows: horizontal item, image beside the text), ' +
          '1 = Card (grid / tiles / cards: vertical item, image on top). Both arrange items in automatic columns ' +
          'based on the widget width; there is no column count, masonry or carousel option, so for such requests ' +
          'offer Strip, Card or resizing the widget. When changing the layout, also set the values the Settings panel ' +
          'sets with it: for Card, itemImageRatio = 2, addToCartButtonPlacement = 0 and style ' +
          'quickAddHorizontalAlignment = 0; for Strip, itemImageRatio = 0, addToCartButtonPlacement = 1 and style ' +
          'quickAddHorizontalAlignment = 2.',
      },
    }),
  },
  itemImagePlacement: {
    type: SettingsParamType.Number,
    getDefaultValue: () => ITEM_IMAGE_PLACEMENT.RIGHT,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: ITEM_IMAGE_PLACEMENT.RIGHT },
      dataItemManifestData: {
        displayName: t('app.settings.layout.menu.image-placement'),
      },
      ai: {
        description:
          'Strip layout only: side of the menu item image, 0 = right (default), 1 = left. ' +
          'Card layout always shows the image on top.',
      },
    }),
  },
  itemImageRatio: {
    type: SettingsParamType.Number,
    // No stored value means the site predates the Image ratio control, so fall
    // back to the shape it already rendered: strip 1:1, card 16:9. This is what
    // keeps existing card sites off 1:1 — the viewer reads these settings
    // ungated, so a flat SQUARE default would reflow every card site on deploy.
    getDefaultValue: ({ getSettingParamValue }) =>
      getSettingParamValue(itemLayoutTypeParam) === ITEM_LAYOUT_TYPE.CARD
        ? ITEM_IMAGE_RATIO.WIDE
        : ITEM_IMAGE_RATIO.SQUARE,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: ITEM_IMAGE_RATIO.SQUARE },
      dataItemManifestData: {
        displayName: t('app.settings.layout.menu.image-ratio'),
      },
      ai: {
        description:
          'Menu item image shape. The same number means a different shape per layout. ' +
          'Card: 0 = square 1:1, 1 = landscape 4:3, 2 = wide 16:9 (Card default). ' +
          'Strip: 0 = square 1:1 (Strip default), 1 = portrait 3:4, 2 = tall 9:16. ' +
          'Strip has no landscape shapes and Card has no portrait shapes. ' +
          'If this was never changed on a Card layout, the widget shows 16:9 even when the reported value is 0.',
      },
    }),
  },
  itemImageResize: {
    type: SettingsParamType.Number,
    getDefaultValue: () => ITEM_IMAGE_RESIZE.CROP,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: ITEM_IMAGE_RESIZE.CROP },
      dataItemManifestData: {
        displayName: t('app.settings.layout.menu.resize-options'),
      },
      ai: {
        description:
          'How the menu item image fills its frame: 0 = crop to fill the frame (default), 1 = fit the whole image inside the frame.',
      },
    }),
  },
  headerImagePlacement: {
    type: SettingsParamType.Number,
    getDefaultValue: () => HEADER_PLACEMENT.BELOW,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: { type: 'static', value: HEADER_PLACEMENT.BELOW },
      dataItemManifestData: {
        displayName: t('app.settings.layout.header.image'),
      },
      ai: {
        description:
          'Online Orders header cover image position: 0 = header title and description below the image (default), ' +
          '1 = header title and description on top of the image, the image used as a background.',
      },
    }),
  },
  addToCartButtonPlacement: {
    type: SettingsParamType.Number,
    // Desktop and mobile intentionally resolve to different defaults for the
    // same layout (see getDefaultValue below), so mobile must never fall back
    // to desktop's stored/default value — it always computes its own.
    inheritDesktop: false,
    // Strip has room for the button over the image only on desktop; Card and
    // mobile always keep it in the item info (mirrors handleStripLayoutClick's
    // interactive default in Menu/index.tsx).
    getDefaultValue: ({ isMobile, getSettingParamValue }) =>
      !isMobile &&
      getSettingParamValue(itemLayoutTypeParam) === ITEM_LAYOUT_TYPE.STRIP
        ? ADD_TO_CART_BUTTON_PLACEMENT.ON_IMAGE
        : ADD_TO_CART_BUTTON_PLACEMENT.ITEM_INFO,
    metadata: ({ t }) => ({
      type: DataType.number,
      defaultValue: {
        type: 'static',
        value: ADD_TO_CART_BUTTON_PLACEMENT.ON_IMAGE,
      },
      mobileDefaultValue: {
        type: 'static',
        value: ADD_TO_CART_BUTTON_PLACEMENT.ITEM_INFO,
      },
      dataItemManifestData: {
        displayName: t(
          'app.settings.layout.menu.add-to-cart-button.button-placement',
        ),
      },
      ai: {
        description:
          'Add to cart button position on menu items: 0 = in the item info, 1 = over the item image. ' +
          'Strip layout on mobile always shows it in the item info. When changing it, also set the styles ' +
          'the Settings panel sets with it: quickAddButtonWidth = 1 (fit to text) and quickAddHorizontalAlignment = 0 ' +
          'for item info or 2 for over the image. ' +
          'If this was never changed on a Card layout, the widget shows it in the item info even when the reported value is 1.',
      },
    }),
  },
});
